
















































































































































































































import { isViewAsSelected } from "@/store/modules/TargetUserModule";
import { Component, Vue, Ref, Watch, Prop } from "vue-property-decorator";
import BaseDialogComponent from "@/components/common/BaseDialog.vue";
import { filterOutUndefined, generateRandomUuid, getEmailRules } from "@/utils/Utils";
import ConfirmationDialogComponent from "@/components/common/ConfirmationDialog.vue";
import { SnackbarModule } from "@/store/modules/SnackbarModule";
import { Branch, getBranchName, UNASSIGNED_BRANCH_NUMBER } from "@/data/models/Branch";
import PickerDialogComponent from "@/components/common/PickerDialog.vue";
import LogoPreviewComponent from "@/components/common/LogoPreview.vue";
import AddressPositionFieldComponent from "@/components/common/AddressPositionField.vue";
import { GeoLocation, PickedAddressParts, PickerAddress } from "@/utils/GeocodingService";
import { hasCoordinatesChanged } from "@/utils/UserFormUtils";
import { addressPositionAccess } from "@/permissions/UserPermissions";
import {
  getBranchesOfClient,
  getClientInfo,
  saveBranch,
  updateBranchInformationForGroupIfNecessary,
} from "@/data/repository/ClientRepository";
import { uploadImage, getImageUrl } from "@/data/repository/FirebaseImageRepository";
import { BranchesModule } from "@/store/modules/BranchesModule";
import { updateIresDeviceBranch } from "@/data/repository/IresDeviceRepository";
import { updateClientPanelsBranch } from "@/data/repository/ClientPanelRepository";
import ClientPanel from "@/data/models/ClientPanel";
import { UserSettingsModule } from "@/store/modules/UserSettingsModule";
import { getEffectiveUser } from "@/utils/ViewAs";

interface BranchDetails {
  branchId: string;
  name: string;
  branchNumber: string;
  branchSortValue: string;
  fullName: string;
  countryCode: string;
  zipCode: string;
  city: string;
  street: string;
  houseNumber: string;
  useInReport: boolean;
  emailAddress?: string | null;
  phone?: string | null;
  mobilePhone?: string | null;
  logoFile?: string | null;
  logoScaleFactor?: number;
}

@Component({
  components: {
    "base-dialog": BaseDialogComponent,
    picker: PickerDialogComponent,
    "confirmation-dialog": ConfirmationDialogComponent,
    "logo-preview": LogoPreviewComponent,
    "address-position-field": AddressPositionFieldComponent,
  },
})

export default class BranchFormComponent extends Vue {
  @Prop()
  clientPanels!: ClientPanel[];

  @Prop({ default: false })
  disableBranchDeletion: boolean;

  @Ref("baseDialog")
  baseDialogComponent!: BaseDialogComponent;

  @Ref("confirmation")
  confirmationDialogComponent!: ConfirmationDialogComponent;

  @Ref("logoPreview")
  logoPreviewComponent!: LogoPreviewComponent;

  isLoading = false;
  isEditMode = false;

  branchBefore: Branch | null = null;
  branchId = "";

  name = "";
  branchNumber = "";
  branchSortValue = "";
  branchNumberErrors: string[] = [];

  fullName = "";
  countryCode = "";
  zipCode = "";
  city = "";
  street = "";
  houseNumber = "";

  /**
   * Where this site actually is.
   *
   * Stored on the branch so IRES Work can pin it without geocoding the address again on
   * every drive; nothing wrote it before, which is why the Büro destination could never
   * centre the map.
   */
  coordinates: GeoLocation | null = null;
  emailAddress = "";
  phone = "";
  mobilePhone = "";

  logoFile: string | null = null;
  newLogoFile: File | null = null;
  logoScaleFactor = 100;
  logoRemoved = false;
  isLogoDragging = false;
  resolvedLogoUrl: string | null = null;

  useInReport = true;
  isNewBranch = false;

  yesNoValues = [true, false];

  emailRules = getEmailRules(this.$t("common.invalidEmailAddress").toString());

  initialBranchDetails: BranchDetails | null = null;
  snapshot: BranchDetails | null = null;



  cancelButtonText = this.$t("common.abort").toString();
  closeButtonText = this.$t("common.close").toString();
  editButtonText = this.$t("settings.buttons.change").toString();
  saveButtonText = this.$t("common.save").toString();

  resolve?: (value: Branch) => void = undefined;
  reject?: (reason: string) => void = undefined;

  formatYesNo(value: boolean) {
    return this.$t(`common.${value ? "yes" : "no"}`).toString();
  }

  @Watch("hasDetailsEdited")
  onDetailsEdited() {
    if (this.isEditMode) {
      const neutralText = this.calculateNeutralButtonText();
      this.baseDialogComponent.neutralPositiveButtonText = neutralText ?? "";
      this.baseDialogComponent.showNeutralPositiveButton = !!neutralText;
      this.baseDialogComponent.negativeButtonText = this.calculateNegativeButtonText();
    }
  }

  get clientPanelsAssignedToBranch() {
    return this.clientPanels.filter((clientPanel) => clientPanel.branch === this.branchNumber?.toString());
  }

  get noOfClientPanelsASsignedToBranch() {
    return this.clientPanelsAssignedToBranch.length;
  }

  get strictBranchFilters() {
    return UserSettingsModule.userSettings.strictBranchFilters ?? false;
  }

  get isMandatoryDataFilled() {
    return this.branchNumber !== "" && this.name !== "";
  }

  get hasDetailsEdited() {
    if (!this.initialBranchDetails) {
      return false;
    }

    return this.name !== this.initialBranchDetails.name ||
      this.branchNumber !== this.initialBranchDetails.branchNumber ||
      this.branchSortValue !== this.initialBranchDetails.branchSortValue ||
      this.fullName !== this.initialBranchDetails.fullName ||
      this.countryCode !== this.initialBranchDetails.countryCode ||
      this.zipCode !== this.initialBranchDetails.zipCode ||
      this.city !== this.initialBranchDetails.city ||
      this.street !== this.initialBranchDetails.street ||
      this.houseNumber !== this.initialBranchDetails.houseNumber ||
      hasCoordinatesChanged(this.coordinates, this.branchBefore?.coordinates ?? null) ||
      this.useInReport !== this.initialBranchDetails.useInReport ||
      this.emailAddress !== this.initialBranchDetails.emailAddress ||
      this.phone !== this.initialBranchDetails.phone ||
      this.mobilePhone !== (this.initialBranchDetails.mobilePhone ?? "") ||
      this.newLogoFile !== null ||
      this.logoRemoved ||
      this.logoScaleFactor !== (this.initialBranchDetails.logoScaleFactor ?? 100);
  }

  calculateNegativeButtonText() {
    if (!this.isEditMode) {
      return this.closeButtonText;
    }
    return this.hasDetailsEdited
      ? this.cancelButtonText
      : this.closeButtonText;
  }

  calculateNeutralButtonText() {
    if (!this.isEditMode) {
      return undefined;
    }

    if (this.isNewBranch || this.disableBranchDeletion) {
      return undefined;
    }

    return this.hasDetailsEdited
      ? undefined
      : this.$t("common.delete").toString();
  }

  calculatePositiveButtonText() {
    return this.isEditMode ? this.saveButtonText : this.editButtonText;
  }

  takeSnapshot() {
    this.snapshot = {
      branchId: this.branchId,
      name: this.name,
      branchNumber: this.branchNumber,
      branchSortValue: this.branchSortValue,
      fullName: this.fullName,
      countryCode: this.countryCode,
      zipCode: this.zipCode,
      city: this.city,
      street: this.street,
      houseNumber: this.houseNumber,
      useInReport: this.useInReport,
      emailAddress: this.emailAddress,
      phone: this.phone,
      mobilePhone: this.mobilePhone,
    };
  }

  restoreSnapshot() {
    if (!this.snapshot) return;
    this.name = this.snapshot.name;
    this.branchNumber = this.snapshot.branchNumber;
    this.branchSortValue = this.snapshot.branchSortValue;
    this.fullName = this.snapshot.fullName;
    this.countryCode = this.snapshot.countryCode;
    this.zipCode = this.snapshot.zipCode;
    this.city = this.snapshot.city;
    this.street = this.snapshot.street;
    this.houseNumber = this.snapshot.houseNumber;
    this.useInReport = this.snapshot.useInReport;
    this.emailAddress = this.snapshot.emailAddress ?? "";
    this.phone = this.snapshot.phone ?? "";
    this.mobilePhone = this.snapshot.mobilePhone ?? "";
  }

  enterEditMode() {
    this.isEditMode = true;
    this.takeSnapshot();
    this.updateDialogButtons();
    this.baseDialogComponent.title = this.calculateTitle();
    this.baseDialogComponent.persistent = true;
  }

  exitEditMode() {
    this.isEditMode = false;
    this.updateDialogButtons();
    this.baseDialogComponent.title = this.calculateTitle();
    this.baseDialogComponent.persistent = false;
  }

  calculateTitle(): string {
    return this.isEditMode
      ? this.$t("settings.branches.editTitle").toString()
      : this.$t("settings.branches.viewTitle").toString();
  }

  updateDialogButtons() {
    const neutralText = this.calculateNeutralButtonText();
    this.baseDialogComponent.positiveButtonText = this.calculatePositiveButtonText();
    this.baseDialogComponent.negativeButtonText = this.calculateNegativeButtonText();
    this.baseDialogComponent.neutralPositiveButtonText = neutralText ?? "";
    this.baseDialogComponent.showNeutralPositiveButton = !!neutralText;
  }

  onPositiveAction() {
    if (!this.isEditMode) {
      this.enterEditMode();
    } else {
      this.onSave();
    }
  }

  async onNegativeAction() {
    if (!this.isEditMode) {
      // View mode: just close
      this.baseDialogComponent.isShowing = false;
      this.initialBranchDetails = null;
      this.isNewBranch = false;
    } else {
      // Edit mode: abort with confirmation if changes exist
      await this.onAbortEditBranchDetails();
    }
  }

  initializeBranchDetails(branchDetails: BranchDetails) {
    this.branchId = branchDetails.branchId ?? generateRandomUuid();
    this.name = branchDetails.name
    this.branchNumber = branchDetails.branchNumber;
    this.branchSortValue = branchDetails.branchSortValue;
    this.fullName = branchDetails.fullName;
    this.countryCode = branchDetails.countryCode;
    this.zipCode = branchDetails.zipCode;
    this.city = branchDetails.city;
    this.street = branchDetails.street;
    this.houseNumber = branchDetails.houseNumber;
    // Reset here, restored below for an existing site: this runs before `branchBefore` is
    // assigned, so reading the stored position from it here would always find nothing and
    // quietly drop the site's position on the next save.
    this.coordinates = null;
    this.useInReport = branchDetails.useInReport;
    this.emailAddress = branchDetails.emailAddress ?? "";
    this.phone = branchDetails.phone ?? "";
    this.mobilePhone = branchDetails.mobilePhone ?? "";
    this.logoFile = branchDetails.logoFile ?? null;
    this.logoScaleFactor = branchDetails.logoScaleFactor ?? 100;
    this.newLogoFile = null;
    this.logoRemoved = false;
    this.resolveLogoUrl();

    this.initialBranchDetails = branchDetails;
  }

  onAbortEditBranchDetailsConfirmed() {
    this.baseDialogComponent.isShowing = false;
    this.initialBranchDetails = null;
    this.isNewBranch = false;
  }

  async onAbortEditBranchDetails() {
    if (this.hasDetailsEdited) {
      const result = await this.confirmationDialogComponent.show({
        title: this.$t("common.confirmationDialog.unsavedChangesTitle").toString(),
        message: this.$t("common.confirmationDialog.unsavedChangesMessage").toString(),
        rejectMessage: this.$t("common.back").toString(),
        acceptMessage: this.$t("common.abort").toString(),
      });

      if (result) {
        this.onAbortEditBranchDetailsConfirmed();
      }
    }
    else {
      this.onAbortEditBranchDetailsConfirmed();
    }
  }

  async show(branch?: Branch) {
    this.branchNumberErrors = [];

    if (branch) {
      this.initializeBranchDetails({
        branchId: branch.id,
        name: branch.name,
        branchNumber: branch.branchNumber.toString(),
        branchSortValue: branch.branchSortValue?.toString() ?? this.branchNumber,
        fullName: branch.fullName ?? "",
        countryCode: branch.countryCode ?? "",
        zipCode: branch.zipCode ?? "",
        city: branch.city ?? "",
        street: branch.street ?? "",
        houseNumber: branch.houseNumber ?? "",
        useInReport: branch.useInReport ?? true,
        emailAddress: branch.emailAddress ?? "",
        phone: branch.phone ?? "",
        mobilePhone: branch.mobilePhone ?? "",
        logoFile: branch.logoFile ?? null,
        logoScaleFactor: branch.logoScaleFactor ?? 100,
      });

      this.branchBefore = branch;
      this.coordinates = branch.coordinates ?? null;
      this.isEditMode = false;
    } else {
      this.initializeBranchDetails({
        branchId: generateRandomUuid(),
        name: "",
        branchNumber: "",
        branchSortValue: "1",
        fullName: "",
        countryCode: "",
        zipCode: "",
        city: "",
        street: "",
        houseNumber: "",
        useInReport: true,
        emailAddress: "",
        phone: "",
        mobilePhone: "",
      });

      this.isNewBranch = true;
      this.isEditMode = true;
      this.takeSnapshot();
    }

    this.baseDialogComponent.show({
      title: this.calculateTitle(),
      // View only while "Sicht auf" is active (ticket 2610051).
      positiveButtonText: isViewAsSelected() ? null : this.calculatePositiveButtonText(),
      neutralPositiveButtonText: this.calculateNeutralButtonText(),
      negativeButtonText: this.calculateNegativeButtonText(),
      closeOnNeutralPositiveAction: false,
      closeOnSave: false,
      persistent: this.isEditMode,
    });

    return new Promise<Branch>((resolve, reject) => {
      this.resolve = resolve;
      this.reject = reject;
    });
  }

  async onDeleteBranch() {
    if (!this.branchBefore) {
      return;
    }

    if (this.noOfClientPanelsASsignedToBranch !== 0) {
      if (!this.strictBranchFilters) {
        const didConfirmDeletion = await this.confirmationDialogComponent.show({
          title: this.$t("settings.branches.confirmDeleteBranchTitle").toString(),
          message: this.$tc(
            "settings.branches.confirmDeleteBranchMessageAlt",
            this.noOfClientPanelsASsignedToBranch
          ).toString(),
        });

        if (!didConfirmDeletion) {
          return;
        }
      }
      else {
        await this.confirmationDialogComponent.show({
          title: this.$t("settings.branches.confirmDeleteBranchTitle").toString(),
          message: this.$tc(
            "settings.branches.rejectDeleteBranchMessage",
            this.noOfClientPanelsASsignedToBranch
          ).toString(),
          rejectMessage: null,
          acceptMessage: this.$t("common.ok").toString(),
        });

        return;
      }
    }
    else {
      const didConfirmDeletion = await this.confirmationDialogComponent.show({
        title: this.$t("settings.branches.confirmDeleteBranchTitle").toString(),
        message: this.$t(
          "settings.branches.confirmDeleteBranchMessage"
        ).toString(),
      });

      if (!didConfirmDeletion) {
        return;
      }
    }

    this.isLoading = true;

    const deletedBranch = {
      ...this.branchBefore,
      deleted: true
    };

    const branchNumber = deletedBranch.branchNumber.toString();

    const promises = [
      saveBranch(deletedBranch),
      updateBranchInformationForGroupIfNecessary(deletedBranch),
      updateClientPanelsBranch(branchNumber, UNASSIGNED_BRANCH_NUMBER),
      updateIresDeviceBranch(branchNumber, UNASSIGNED_BRANCH_NUMBER)
    ];

    await Promise.all(promises);

    SnackbarModule.showSuccess({ key: "common.branchDeleted" });

    this.initialBranchDetails = null;
    this.isLoading = false;
    this.baseDialogComponent.isShowing = false;
  }

  get branchesOfGroup() {
    const branchesOfGroup = BranchesModule.branchesOfGroup;

    return branchesOfGroup.length !== 0
      ? branchesOfGroup
      : null;
  }

  get logoPreviewUrl(): string | null {
    if (!this.newLogoFile) return null;
    return URL.createObjectURL(this.newLogoFile);
  }

  async resolveLogoUrl() {
    if (!this.logoFile) {
      this.resolvedLogoUrl = null;
      return;
    }
    try {
      this.resolvedLogoUrl = await getImageUrl(this.logoFile);
    } catch {
      this.resolvedLogoUrl = null;
    }
  }

  onLogoSlotClick() {
    if (!this.isEditMode) return;
    this.logoPreviewComponent.showExpandDialog = true;
  }

  onLogoDragOver() {
    if (this.isEditMode) {
      this.isLogoDragging = true;
    }
  }

  onLogoDrop(event: DragEvent) {
    this.isLogoDragging = false;
    if (!this.isEditMode) return;
    const file = event.dataTransfer?.files[0];
    if (file && (file.type === "image/jpeg" || file.type === "image/png")) {
      this.logoPreviewComponent.trimAndEmit(file);
    }
  }

  onLogoUpload(file: File) {
    this.newLogoFile = file;
    this.logoRemoved = false;
  }

  onRemoveLogo() {
    this.newLogoFile = null;
    this.logoRemoved = true;
    this.logoFile = null;
    this.resolvedLogoUrl = null;
  }

  /** Whether geocoding is switched on for this client at all. */
  get enableGeoEncoding(): boolean {
    // Reactive: a capability change on the current user must reach this getter without a
    // page refresh (see the lessons in CLAUDE.md).
    return addressPositionAccess(getEffectiveUser());
  }

  /** The site address as currently entered, for the map affordance. */
  get enteredAddress(): PickerAddress {
    return {
      street: this.street,
      houseNumber: this.houseNumber,
      zipCode: this.zipCode,
      city: this.city,
      country: this.countryCode,
      // A branch states its own country, so the only fallback is the company: 112 of 319 live
      // branches leave `countryCode` empty, and a further 11 hold something unreadable.
      countryFallbacks: [getClientInfo()?.country],
    };
  }

  /**
   * The site address as it was when editing began.
   *
   * A pin placed for it must not decide where the map opens once the address has been
   * edited. In view mode nothing can have been edited, so there is nothing to compare
   * against.
   */
  /**
   * Fills in the address parts a lookup or the map found, for the ones that were left empty:
   * the postcode and city from the automatic lookup, or the whole address from a pin placed
   * on the map while the address was still empty (ticket 2610025).
   *
   * Only ever fills a blank: a value the user typed stands, even where Google disagrees.
   */
  onAddressResolved(parts: PickedAddressParts) {
    if (parts.street && !this.street) {
      this.street = parts.street;
    }

    if (parts.houseNumber && !this.houseNumber) {
      this.houseNumber = parts.houseNumber;
    }

    if (parts.zipCode && !this.zipCode) {
      this.zipCode = parts.zipCode;
    }

    if (parts.city && !this.city) {
      this.city = parts.city;
    }

    if (parts.country && !this.countryCode) {
      this.countryCode = parts.country;
    }
  }

  get savedAddress(): PickerAddress | undefined {
    return this.isEditMode
      ? {
        street: this.snapshot?.street,
        houseNumber: this.snapshot?.houseNumber,
        zipCode: this.snapshot?.zipCode,
        city: this.snapshot?.city,
        country: this.snapshot?.countryCode,
      }
      : undefined;
  }

  async onSave() {
    const branchNumber = Number(this.branchNumber ?? null);

    if (!this.branchNumber || isNaN(branchNumber)) {
      this.branchNumberErrors = [
        this.$t("settings.branches.errorMissingBranchNumber").toString(),
      ];

      return;
    }

    const existingBranches = this.branchesOfGroup ?? getBranchesOfClient();
    const otherBranchWithBranchNumber = existingBranches.find((branch) => {
      return (
        branch.id !== this.branchId && branch.branchNumber === branchNumber
      );
    });

    if (otherBranchWithBranchNumber) {
      this.branchNumberErrors = [
        this.$t("settings.branches.errorDuplicateBranchNumber", [
          getBranchName(otherBranchWithBranchNumber),
        ]).toString(),
      ];

      return;
    }

    this.isLoading = true;

    // Handle logo upload/removal
    let logoFile = this.logoFile;
    if (this.newLogoFile) {
      const parts = this.newLogoFile.name.split(".");
      const extension = parts[parts.length - 1] ?? "png";
      const fileName = generateRandomUuid() + "." + extension;
      await uploadImage(fileName, this.newLogoFile);
      logoFile = fileName;
    } else if (this.logoRemoved) {
      logoFile = null;
    }

    const branch: Branch = filterOutUndefined({
      id: this.branchId,

      branchNumber: branchNumber,
      branchSortValue: Number(this.branchSortValue) ?? 1,

      name: this.name,
      fullName: this.fullName,
      useInReport: this.useInReport,

      countryCode: this.countryCode,
      zipCode: this.zipCode,
      city: this.city,
      street: this.street,
      houseNumber: this.houseNumber,
      // Omitted rather than nulled when there is none: null is the "looked up, no answer"
      // tombstone in this model, and claiming that would stop it ever being looked up.
      ...(this.coordinates ? { coordinates: this.coordinates } : {}),

      emailAddress: this.emailAddress !== ""
        ? this.emailAddress
        : null,
      phone: this.phone !== ""
        ? this.phone
        : null,
      mobilePhone: this.mobilePhone !== ""
        ? this.mobilePhone
        : null,
      logoFile: logoFile ?? null,
      logoScaleFactor: this.logoScaleFactor,
    });

    const promises = [
      saveBranch(branch),
      updateBranchInformationForGroupIfNecessary(branch),
    ];

    if (this.branchNumber !== this.initialBranchDetails?.branchNumber) {
      const oldBranchNumber = this.initialBranchDetails?.branchNumber ?? "0";
      const newBranchNumber = this.branchNumber;
      
      promises.push(updateClientPanelsBranch(oldBranchNumber, newBranchNumber));
      promises.push(updateIresDeviceBranch(oldBranchNumber, newBranchNumber));
    }

    await Promise.all(promises);

    SnackbarModule.showSuccess({ key: "common.changesSaved" });

    this.initialBranchDetails = null;
    this.isLoading = false;
    this.baseDialogComponent.isShowing = false;
    this.resolve && this.resolve(branch);
  }
}
